import { useCallback, useEffect, useRef, useState } from "react"

import api from "../../../api.js"
import { OUTBOX_SYNCED_EVENT } from "../lib/outbox.js"

// The plan changes when dispatch assigns work or the driver finishes a stop.
// Polling this slowly is enough; returning to the tab refreshes immediately.
const POLL_INTERVAL_MS = 60000

/**
 * Loads today's route plan and keeps it current.
 *
 * The dashboard previously fetched once on mount, so a driver who left the app
 * open saw stale assignments for the rest of the shift.
 */
export function useDriverPlan() {
  const [stops, setStops] = useState([])
  const [currentPaymentId, setCurrentPaymentId] = useState(null)
  const [branch, setBranch] = useState(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState(null)

  // Distinguishes the first load (full-screen spinner) from background
  // refreshes (quiet indicator, existing list stays on screen).
  const loadedOnceRef = useRef(false)

  const load = useCallback(async ({ background = false } = {}) => {
    try {
      if (background) setRefreshing(true)
      else setLoading(true)

      const res = await api.get("/api/driver/plan/today")
      const data = res?.data?.data || null

      const nextStops = Array.isArray(data?.stops) ? data.stops : []

      // Flatten to what the list renders, keeping sequence and lock state.
      setStops(
        nextStops
          .map((s) => {
            if (!s?.job) return null
            return {
              ...s.job,
              sequence: s.sequence,
              stopStatus: s.status,
              failure: s.failure || null,
              canStart: Boolean(s.canStart),
            }
          })
          .filter(Boolean),
      )

      setCurrentPaymentId(data?.currentPaymentId != null ? Number(data.currentPaymentId) : null)
      setBranch(data?.branch || null)
      setError(null)
      loadedOnceRef.current = true
    } catch (err) {
      console.error("[useDriverPlan] load:", err)
      // A failed background poll shouldn't wipe a list the driver is using.
      if (!loadedOnceRef.current) setError(err?.message || "Failed to load today's route")
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") load({ background: true })
    }, POLL_INTERVAL_MS)

    // Coming back to the app is the moment stale data is most obvious.
    const onVisible = () => {
      if (document.visibilityState === "visible") load({ background: true })
    }

    // Reloading on "online" isn't enough on its own: the queue is still
    // draining at that point, so the server hasn't applied the change yet.
    // This fires once the replay has actually landed.
    const onSynced = () => load({ background: true })

    document.addEventListener("visibilitychange", onVisible)
    window.addEventListener("online", onVisible)
    window.addEventListener(OUTBOX_SYNCED_EVENT, onSynced)

    return () => {
      clearInterval(interval)
      document.removeEventListener("visibilitychange", onVisible)
      window.removeEventListener("online", onVisible)
      window.removeEventListener(OUTBOX_SYNCED_EVENT, onSynced)
    }
  }, [load])

  return {
    stops,
    currentPaymentId,
    branch,
    loading,
    refreshing,
    error,
    refresh: () => load({ background: true }),
  }
}

export default useDriverPlan
